import { Box, Square, Text, VStack } from '@chakra-ui/react'
import { MeasurementLabelProps } from '@editor/interfaces/canvas/measurementLabel'
import { getDistanceLabel } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setEditSelectedElementId } from '@editor/store/tool'
import { StylesProvider } from '@infraloop/common'
import { Html } from '@react-three/drei'
import { inRange } from 'lodash'
import { useSelector } from 'react-redux'
import { DoubleSide } from 'three'

import Anchors from './components/Anchors'
import HoverContainer from './components/HoverContainer'
import useAdjustmentHandlers from './hooks/useAdjustmentHandlers'
import useConfig from './hooks/useConfig'
import useObjectsManagement from './hooks/useObjectsManagement'

const MeasurementLabel = (props: MeasurementLabelProps) => {
  const { id, visible = true, editable = false, isEditing = false, isWorking = false } = props

  // Hooks
  const { lineConfig, textConfig } = useConfig(props)
  const objects = useObjectsManagement(props)
  const {
    offsetPosition,

    currentPoints,
    containerPoints,
    labelPosition,

    lineObject,
    offsetLineObject,
    arrowGeometries,
  } = objects
  const {
    distance,
    isDraggingLabelRef,
    isAdjustingAnchorRef,
    isMovingRef,
    containerRef,
    labelRef,
    labelTopLeftRef,
    labelBottomLeftRef,
  } = useAdjustmentHandlers(props, objects)

  // Store
  const dispatch = useAppDispatch()
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const currentAnchorPosition = useSelector((state: RootState) => state.toolUtil.core.currentAnchorPosition)
  const currentStrokeColor = useSelector((state: RootState) => state.toolUtil.core.currentStrokeColor)
  const currentTextColor = useSelector((state: RootState) => state.toolUtil.core.currentTextColor)

  // Derived
  const isSelected = selectedElementIds.includes(id)
  const isAdjusting = isWorking || isEditing

  return (
    <group visible={visible} position={offsetPosition}>
      {/* The main measurement line */}
      {lineObject && <primitive object={lineObject} opacity={0.5} />}

      {/* Arrows */}
      <mesh geometry={arrowGeometries[0]}>
        <meshBasicMaterial
          color={(isSelected || isAdjusting) && currentStrokeColor ? currentStrokeColor : lineConfig?.color}
          side={DoubleSide}
          transparent
        />
      </mesh>
      <mesh geometry={arrowGeometries[1]}>
        <meshBasicMaterial
          color={(isSelected || isAdjusting) && currentStrokeColor ? currentStrokeColor : lineConfig?.color}
          side={DoubleSide}
          transparent
        />
      </mesh>

      {/* Labels */}
      <Html
        position={labelPosition.point.toArray()}
        style={{ pointerEvents: 'none', display: visible ? 'block' : 'none' }}
      >
        <StylesProvider>
          <VStack ref={containerRef} transform={`translate(-50%, -50%) rotate(${-labelPosition.angle}deg)`}>
            <Square size={inRange(labelPosition.angle, -90, 90) === labelPosition.direction ? 0 : '30px'} />
            <Box
              ref={labelRef}
              px={3}
              py={1}
              pointerEvents="auto"
              fontSize={`${textConfig?.size}`}
              color={(isSelected || isAdjusting) && currentTextColor ? currentTextColor : textConfig?.color}
              onMouseDown={(e) => {
                e.stopPropagation()
                dispatch(setEditSelectedElementId(id))
                isDraggingLabelRef.current = true
              }}
              position="relative"
              style={
                isDraggingLabelRef.current
                  ? {
                      border: '1px solid #0bc5ea',
                      cursor: 'move',
                    }
                  : undefined
              }
              _hover={
                editable
                  ? {
                      border: '1px solid #0bc5ea',
                      cursor: 'move',
                    }
                  : undefined
              }
            >
              {/* offset by 1px due to border */}
              <Box ref={labelTopLeftRef} data-position="top-left" position="absolute" top={-1} left={-1} />
              <Box ref={labelBottomLeftRef} data-position="bottom-left" position="absolute" bottom={-1} left={-1} />
              <Text>{getDistanceLabel(distance)}</Text>
            </Box>
            <Square size={inRange(labelPosition.angle, -90, 90) !== labelPosition.direction ? 0 : '30px'} />
          </VStack>
        </StylesProvider>
      </Html>

      {/* Offset lines */}
      {offsetLineObject && (
        <>
          <primitive object={offsetLineObject[0]} />
          <primitive object={offsetLineObject[1]} />
        </>
      )}

      {/* Anchors for adjustment */}
      {editable && (
        <Anchors
          points={currentPoints}
          visible={!isAdjusting || (isAdjusting && isAdjustingAnchorRef.current !== null)}
          editable={!isAdjusting}
          id={id}
          onDown={(anchorIndex) => {
            dispatch(setEditSelectedElementId(id))
            isAdjustingAnchorRef.current = anchorIndex
          }}
        />
      )}

      {editable && containerPoints?.length && (
        <HoverContainer
          id={id}
          points={containerPoints.map((p) => p.world)}
          isEditing={isEditing}
          editable={!isAdjusting}
          onDown={() => {
            dispatch(setEditSelectedElementId(id))
            isMovingRef.current = currentAnchorPosition || null
          }}
        />
      )}
    </group>
  )
}

export default MeasurementLabel
